import { useEffect, useState } from "react";
import { ShieldCheck, Loader2, Monitor } from "lucide-react";
import { fetchTosAcceptances } from "@/lib/api";
import EmailHealthCard from "@/components/admin/EmailHealthCard";
import StripeHealthCard from "@/components/admin/StripeHealthCard";

export default function ComplianceTab({ adminKey }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchTosAcceptances(adminKey)
      .then(setData)
      .catch(() => setData({ acceptances: [], current_version: "" }));
  }, [adminKey]);

  if (!data) {
    return (
      <div data-testid="admin-compliance-tab">
        <EmailHealthCard adminKey={adminKey} />
        <div className="mt-6">
          <StripeHealthCard adminKey={adminKey} />
        </div>
        <div className="mt-12 flex items-center gap-3 text-steel-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading acceptance records…
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-compliance-tab">
      <EmailHealthCard adminKey={adminKey} />
      <div className="mt-6">
        <StripeHealthCard adminKey={adminKey} />
      </div>

      <div className="mt-12 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center border border-steel-border">
          <ShieldCheck className="h-5 w-5 text-steel-brass" />
        </div>
        <div>
          <h3 className="font-display text-xl font-medium text-steel-text">Terms of Service acceptances</h3>
          <p className="text-xs text-steel-muted">
            Audit trail of every client sign-in consent · current version{" "}
            <span className="font-mono text-steel-brass">{data.current_version}</span>
          </p>
        </div>
      </div>

      {data.acceptances.length === 0 ? (
        <div className="mt-6 border border-dashed border-steel-border p-8 text-sm text-steel-secondary" data-testid="compliance-empty">
          No acceptance records yet. They appear here each time a client agrees to the Terms at sign-in.
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto border border-steel-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-steel-elevated font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">
              <tr>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Accepted</th>
                <th className="px-4 py-3">Version</th>
                <th className="px-4 py-3">IP address</th>
                <th className="px-4 py-3">Device</th>
              </tr>
            </thead>
            <tbody>
              {data.acceptances.map((a) => (
                <tr key={a.id} className="border-t border-steel-border align-top" data-testid={`tos-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-steel-text">{a.name || "—"}</p>
                    <p className="text-xs text-steel-muted">{a.email}</p>
                  </td>
                  <td className="px-4 py-3 text-steel-secondary">
                    {a.accepted_at ? new Date(a.accepted_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-steel-brass">{a.tos_version}</td>
                  <td className="px-4 py-3 font-mono text-xs text-steel-secondary">{a.ip || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-2 text-xs text-steel-secondary">
                      <Monitor className="mt-0.5 h-3.5 w-3.5 shrink-0 text-steel-muted" />
                      <div>
                        <p>{a.device?.platform || "Unknown platform"} · {a.device?.screen || "—"}</p>
                        <p className="text-steel-muted">{a.device?.timezone || ""} {a.device?.language ? `· ${a.device.language}` : ""}</p>
                        <p className="mt-1 max-w-xs break-words text-[10px] text-steel-muted">{a.user_agent}</p>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
